import { snakeToCamel } from '@rossum/api-client/utils';
import { Stack } from '@rossum/ui/material';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { MIN_FOOTER_HEIGHT } from '../../decorators/makeResizable/config';
import { DocumentCanvas } from '../../features/annotation-view/document-canvas-svg/DocumentCanvas';
import { useCanvasDimensions } from '../../features/annotation-view/document-canvas-svg/useCanvasDimensions';
import { DocumentSidebar } from '../../features/annotation-view/document-sidebar/DocumentSidebar';
import { useDocumentStore } from '../../features/annotation-view/document-store/DocumentStore';
import { SearchPanel } from '../../features/annotation-view/document-topbar/SearchPanel';
import { TopBarV2 } from '../../features/annotation-view/document-topbar/TopBarV2';
import { useTranslationEnabled } from '../../features/annotation-view/document-translation/hooks/useTranslationEnabled';
import { hasDataAvailable } from '../../redux/modules/annotation/helpers';
import {
  annotationSelector,
  pagesSelector,
} from '../../redux/modules/annotation/selectors';
import { State } from '../../types/state';
import { usePreventSwipeNavigation } from '../../utils/hooks/usePreventSwipeNavigation';
import { DrawerConfig } from '../DocumentValidation/ValidationEmailDrawer';
import Footer from '../Footer/Footer';
import {
  getFittingHeightSelector,
  showFooterSelector,
} from '../Footer/selectors';
import styles from './style.module.sass';

type DocumentV2Props = {
  onEmailThreadOpen: (drawerConfig?: DrawerConfig) => void;
};

export const DocumentV2 = ({ onEmailThreadOpen }: DocumentV2Props) => {
  const initialHeight = useSelector(getFittingHeightSelector);

  const showFooter = useSelector(showFooterSelector);

  const pages = useSelector(pagesSelector);

  const dimensions = useCanvasDimensions(pages);

  const { targetLanguage } = useDocumentStore(state => state.translationState);

  const showSearchPanel = useSelector(
    (state: State) => state.search.shouldShow
  );

  const documentRef = useRef<HTMLDivElement>(null);

  usePreventSwipeNavigation();

  const isTranslationEnabled = useTranslationEnabled();

  const annotation = useSelector(annotationSelector);

  const annotationLoaded = annotation && typeof annotation.id === 'number';

  const annotationNoAccess =
    annotation &&
    !hasDataAvailable({
      status: snakeToCamel(annotation.status),
      pages: annotation.pages,
      restrictedAccess: annotation.restrictedAccess,
    });

  return (
    <Stack
      sx={{
        height: '100%',
        width: '100%',
      }}
    >
      <TopBarV2 loading={!annotationLoaded} />
      <Stack
        direction="row"
        sx={{ height: 'calc(100% - 48px)', width: '100%', flex: '1 0 auto' }}
      >
        <DocumentSidebar
          annotation={annotation}
          loading={!annotationLoaded}
          noAccess={annotationNoAccess}
          onEmailThreadOpen={onEmailThreadOpen}
        />
        <Stack className={styles.DocumentWrapper} id="document">
          <SearchPanel
            open={showSearchPanel}
            shouldSearchTranslations={isTranslationEnabled && !!targetLanguage}
          />

          <div
            ref={documentRef}
            className={styles.WorkingArea}
            data-tourstep="dataCaptureProductTour-document"
          >
            <DocumentCanvas
              pages={pages}
              data-tourstep="dataCaptureProductTour-missing-bbox"
              dimensions={dimensions}
            />
          </div>
          {showFooter && (
            <Footer minHeight={MIN_FOOTER_HEIGHT} height={initialHeight} />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
